/**
 * What each job on a list is worth.
 *
 * The estimate card runs the whole ladder — material, labour, expenses,
 * returns, margin — because that is what the card is for. A list of two hundred
 * jobs cannot do that: it would be tens of thousands of rows over the wire to
 * print one figure per line.
 *
 * So the database adds up the four sums a contract is made of, one row per
 * active estimate (`job_contract_parts`), and the arithmetic stays here where
 * the rest of the money lives. `contractAmount` is the same function
 * `computeLadder` uses, so a pipeline card and the estimate it came from can
 * never quietly disagree — CLAUDE.md rule 1, and the reason this is not a
 * second sum written in SQL.
 *
 * A job that sells two options has two active estimates and is worth both. The
 * GC fee is worked out per estimate rather than on the pair, because that is
 * what each estimate is actually billed.
 */

import { contractAmount } from "./ladder.js";
import { addCents, cents, toCents, type Cents } from "./money.js";
import {
  contractAsSigned,
  type SignedContract,
  type SoldOption,
} from "./quickbooks.js";

/** One row of `job_contract_parts`. Values arrive as the database wrote them. */
export interface JobContractParts {
  job_id: string;
  estimate_id?: string;
  line_total: unknown;
  line_discount: unknown;
  change_orders: unknown;
  office_discount: unknown;
}

/** What the caller knows about the job itself. A `Job` satisfies it. */
export interface JobValueJob {
  id: string;
  job_type?: string | null;
}

/**
 * Contract dollars per job id.
 *
 * Every job in `jobs` gets an entry, including the ones with no estimate yet —
 * zero is the honest answer for a lead nobody has priced, and a missing key
 * would read as "unknown" everywhere it was used.
 */
export function jobContractValues(
  jobs: readonly JobValueJob[],
  parts: readonly JobContractParts[],
): Map<string, Cents> {
  const jobTypeOf = new Map(jobs.map((job) => [job.id, job.job_type ?? null]));

  const perJob = new Map<string, Cents[]>();
  for (const row of parts) {
    if (!jobTypeOf.has(row.job_id)) continue;
    const { grandTotal } = contractAmount({
      lineTotal: toCents(row.line_total),
      lineDiscount: toCents(row.line_discount),
      changeOrders: toCents(row.change_orders),
      officeDiscount: toCents(row.office_discount),
      jobType: jobTypeOf.get(row.job_id),
    });
    const list = perJob.get(row.job_id);
    if (list) list.push(grandTotal);
    else perJob.set(row.job_id, [grandTotal]);
  }

  const values = new Map<string, Cents>();
  for (const job of jobs) {
    values.set(job.id, addCents(...(perJob.get(job.id) ?? [])));
  }
  return values;
}

/** Deposit bases for signed jobs, using the same compact option totals as lists. */
export function signedJobContractValues(
  jobs: readonly JobValueJob[],
  parts: readonly JobContractParts[],
  signatures: ReadonlyMap<string, readonly SignedContract[]>,
): Map<string, Cents> {
  const jobTypeOf = new Map(jobs.map((job) => [job.id, job.job_type ?? null]));
  const options = new Map<string, SoldOption[]>();
  for (const row of parts) {
    if (!jobTypeOf.has(row.job_id) || !row.estimate_id) continue;
    const { grandTotal } = contractAmount({
      lineTotal: toCents(row.line_total),
      lineDiscount: toCents(row.line_discount),
      changeOrders: toCents(row.change_orders),
      officeDiscount: toCents(row.office_discount),
      jobType: jobTypeOf.get(row.job_id),
    });
    const list = options.get(row.job_id) ?? [];
    list.push({ estimateId: row.estimate_id, ladderTotal: grandTotal });
    options.set(row.job_id, list);
  }
  const values = new Map<string, Cents>();
  for (const job of jobs) {
    const signed = signatures.get(job.id);
    if (signed?.length) {
      values.set(
        job.id,
        contractAsSigned({ signed, options: options.get(job.id) }),
      );
    }
  }
  return values;
}

/**
 * What a card shows where a job has no price on it yet.
 *
 * Shown rather than left blank: an unpriced lead in the middle of the pipeline
 * is a thing to go and do, and a gap on the card reads as a bug.
 */
export const NO_VALUE_LABEL = "No price yet";

/** Is there a contract figure worth printing? */
export const hasValue = (value: Cents | undefined): boolean =>
  (value ?? cents(0)) > 0;
